import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

/**
 * Handles the Shopify / Wix OAuth redirect for users who initiate the connection
 * from the CMS Dashboard page (/dashboard/cms).
 *
 * Flow:
 *  1. CmsDashboardPage saves `cms_oauth_platform` to sessionStorage and redirects
 *     the user to Shopify/Wix with redirect_uri = <origin>/cms/callback
 *  2. Shopify/Wix redirects back here with ?code=...&shop=...&hmac=...&timestamp=...
 *  3. We read the platform from sessionStorage, POST to the backend to exchange
 *     the code for an access token, then navigate back to /dashboard/cms.
 */
export default function CmsOAuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      const code = searchParams.get('code');
      const shop = searchParams.get('shop') || '';
      const hmac = searchParams.get('hmac') || '';
      const timestamp = searchParams.get('timestamp') || '';
      const state = searchParams.get('state') || '';
      const host = searchParams.get('host') || '';
      const shopifyParams = Object.fromEntries(searchParams.entries());

      const platform = sessionStorage.getItem('cms_oauth_platform') || (shop && hmac ? 'shopify' : '');
      const redirectUri = `${window.location.origin}/cms/callback`;

      // Always clean up sessionStorage
      sessionStorage.removeItem('cms_oauth_platform');

      if (!code || !platform) {
        toast.error('CMS connection failed — missing parameters');
        navigate('/dashboard/cms');
        return;
      }

      try {
        if (platform === 'shopify') {
          await api.post('/cms/shopify/callback', {
            code,
            shop,
            redirect_uri: redirectUri,
            hmac,
            timestamp,
            state,
            host,
            shopify_params: shopifyParams,
          });
        } else if (platform === 'wix') {
          await api.post('/cms/wix/callback', { code });
        }

        const label = platform.charAt(0).toUpperCase() + platform.slice(1);
        toast.success(`${label} connected successfully!`);
        navigate('/dashboard/cms');
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to connect CMS');
        navigate('/dashboard/cms');
      }
    };

    processCallback();
  }, [navigate, searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-indigo-50">
      <div className="text-center">
        <Loader2 className="w-12 h-12 animate-spin text-indigo-500 mx-auto mb-4" />
        <p className="text-slate-600">Connecting your CMS…</p>
      </div>
    </div>
  );
}
